// Analytics event schema for the booking widget.
//
// Matches Donald's June 1 spec (`clubchampion-docs/proposals/
// analytics-event-schema-donald.md`). The previous flow used one
// consolidated `booking` event with a `step` parameter; this schema
// splits each step into a distinct named event, with additional
// action events for granular interaction tracking.
//
// Two emit paths today:
//   1. window.dataLayer.push({ event, ...params })
//        Standard GTM convention. CC's existing Web Container will
//        eventually consume these via custom-event triggers with
//        "Send to server container" forwarding to our sGTM.
//   2. Direct first-party POST to the sGTM /g/collect endpoint
//        Temporary transport so we can validate end-to-end against
//        the 2x4 test GA4 property without depending on the Web
//        Container being configured. Removed once Path B is live.

/** Static value pushed on every event per Donald's spec. Bump on dataLayer changes. */
export const SCHEMA_VERSION = 'v2';

export type AnalyticsEventName =
  | 'book_flow_start'
  | 'book_flow_contact_info_complete'
  | 'generate_lead'
  | 'marketing_opt_in'
  | 'book_flow_loc_selected'
  | 'book_flow_loc_change'
  | 'book_flow_date_selected'
  | 'book_flow_time_selected'
  | 'book_flow_loc_type_date_selected'
  | 'book_flow_submit'
  | 'book_flow_confirm_booking'
  | 'book_flow_complete'
  | 'channelmix_conversion'
  | 'booking_error'
  | 'book_flow_click'
  | 'post_booking_completed'
  | 'widget_abandoned';

/**
 * Events that should fire at most once per session. The dispatch
 * module's session-dedup helper consults this set.
 */
export const ONCE_PER_SESSION: ReadonlySet<AnalyticsEventName> = new Set([
  'book_flow_start',
  'book_flow_contact_info_complete',
  'generate_lead',
  'marketing_opt_in',
  'book_flow_loc_type_date_selected',
  'book_flow_submit',
  'book_flow_confirm_booking',
  'book_flow_complete',
]);

export interface PurchaseItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
  quantity?: number;
}

/**
 * Common fields the dispatch module injects automatically (callers
 * don't pass these — they're added at emit time).
 */
export interface AutoInjectedParams {
  schema_version: string;
  /** Salesforce Account ID, set on emit once we have it. Routes to GA4 `uid` (top-level). */
  user_id?: string;
  /** User-scoped ID for the ChannelMix attribution pipeline. Up.* in GA4 collect. */
  channelmix_client_id?: string;
  /** Per-session widget submission ID. Generated in dispatch. WS-<id>. */
  web_submission_id?: string;
}

/**
 * Optional fields any event may carry. Callers can omit these.
 */
export interface BaseEventParams {
  embed_source?: string;
}

export type EventParams =
  | (BaseEventParams & {
      event: 'book_flow_start';
      entry_source?: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_contact_info_complete';
      account_status?: 'created' | 'matched';
      form_id?: string;
      form_name?: string;
    })
  | (BaseEventParams & {
      event: 'generate_lead';
      lead_source?: string;
      value: number;
      currency: string;
    })
  | (BaseEventParams & {
      event: 'marketing_opt_in';
      opt_in_channels: string[];
      source_step?: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_loc_selected';
      location: string;
      location_id: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_loc_change';
      location_from: string;
      location_to: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_date_selected';
      date_selected: string;
      time_zone?: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_time_selected';
      time_selected: string;
      time_zone?: string;
      /**
       * Cumulative state carried for parity with the legacy `booking step=3`
       * reconstruction in sGTM. Legacy step 3 expects location and
       * fitting_selection in its parameter shape; without these the funnel's
       * step-3 breakdowns would show (not set). Required, not optional.
       */
      location: string;
      fitting_selection: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_loc_type_date_selected';
      location: string;
      fitting_selection: string;
      date_selected: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_submit';
      location: string;
      fitting_selection: string;
      date_selected: string;
      time_selected: string;
      hand_dominance?: 'left' | 'right';
      junior_fitting?: boolean;
      referral_method_primary?: string;
      referral_method_secondary?: string;
      value: number;
      currency: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_confirm_booking';
      location: string;
      fitting_selection: string;
      date_selected: string;
      time_selected: string;
      hand_dominance?: 'left' | 'right';
      junior_fitting?: boolean;
      referral_method_primary?: string;
      referral_method_secondary?: string;
      promo_code?: string;
      changed_location: boolean;
      changed_fitting_type: boolean;
      value: number;
      currency: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_complete';
      location: string;
      fitting_selection: string;
      date_selected: string;
      time_selected: string;
      hand_dominance?: 'left' | 'right';
      junior_fitting?: boolean;
      referral_method_primary?: string;
      referral_method_secondary?: string;
      promo_code?: string;
      changed_location: boolean;
      changed_fitting_type: boolean;
      confirmation_number: string;
      a_number: string;
      waitlist: 'scheduled' | 'WL';
      store: string;
      value: number;
      currency: string;
      items: PurchaseItem[];
    })
  | (BaseEventParams & {
      event: 'channelmix_conversion';
      // Intentional duplicate of book_flow_complete minus `store` per
      // Donald's spec / existing ChannelMix attribution convention.
      location: string;
      fitting_selection: string;
      date_selected: string;
      time_selected: string;
      hand_dominance?: 'left' | 'right';
      junior_fitting?: boolean;
      referral_method_primary?: string;
      referral_method_secondary?: string;
      promo_code?: string;
      changed_location: boolean;
      changed_fitting_type: boolean;
      confirmation_number: string;
      a_number: string;
      waitlist: 'scheduled' | 'WL';
      value: number;
      currency: string;
    })
  | (BaseEventParams & {
      event: 'booking_error';
      error_message: string;
    })
  | (BaseEventParams & {
      event: 'book_flow_click';
      content_type: string;
      content_id?: string;
    })
  | (BaseEventParams & {
      event: 'post_booking_completed';
      booking_id: string;
      ball_fitting_interest: boolean;
      sms_optin: boolean;
    })
  | (BaseEventParams & {
      event: 'widget_abandoned';
      last_step: string;
    });
